<template>
    <fieldset class="flex-column-with-gap">
        <legend>{{ props.companyType }}s</legend>
        <div class="flex-row-with-gap">
            <div>Add a {{ props.companyType }} from the data structure:</div>
            <primevue-select
                :options="dataValidationStore.companies"
                filter
                :virtual-scroller-options="{ itemSize: 30 }"
                v-model="newDevloperFromDataStructure" />
            <p-button label="Add" @click="add(newDevloperFromDataStructure, false)" />
        </div>

        <div class="flex-row-with-gap">
            <div>Add a {{ props.companyType }} not in the data structure:</div>
            <p-input-text v-model="newDeveloper" />
            <p-button label="Add" @click="add(newDeveloper, true)" />
        </div>
        <div v-if="localList && localList.length" class="flex-column-with-gap">
            <div v-for="(value, index) in localList" :key="index" class="flex-row-with-gap">
                <template v-if="value.startsWith('NEW COMPANY: ')">
                    <div>NEW COMPANY:</div>
                    <p-input-text
                        :model-value="stripPrefix(value)"
                        @update:model-value="(val) => debouncedUpdateNewCompanyName(val, index)"
                        :modelModifiers="{ trim: true }"
                    />
                </template>
                <template v-else>
                    {{ value }}
                </template>
                <p-button label="Delete" class="p-button-danger" @click="deleteAt(index)" />
            </div>
        </div>
        <div>
            <p-button label="Delete all" class="p-button-danger" @click="deleteAll" />
        </div>
    </fieldset>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import { UseDataValidationStore } from '@/stores/storageTracking/DataValidationStore.ts';
import { useDebounceFn } from '@vueuse/core';
import PButton from 'primevue/button';
import PrimevueSelect from 'primevue/select';
import PInputText from 'primevue/inputtext';

const dataValidationStore = UseDataValidationStore();

const props = defineProps<{
    companyType: string;
    modelValue?: Array<string>;
}>();

const emit = defineEmits<{(e: 'update:modelValue', value: Array<string>): void;
}>();

const localList = ref<Array<string>>(props.modelValue ? [...props.modelValue] : []);
const newDeveloper = ref('');
const newDevloperFromDataStructure = ref<string | null>(null);

function add (companyName: string | null, companyIsNew: boolean): void {
    if (!companyName) return;
    const cleanCompany = companyName.trim();
    if (!cleanCompany) return;

    const existingCompany: string | undefined = dataValidationStore.companies.find(
        c => c.toLowerCase() === cleanCompany.toLowerCase(),
    );

    if (existingCompany) {
        localList.value = [
            ...localList.value,
            existingCompany,
        ];
    } else {
        localList.value = [
            ...localList.value,
            companyIsNew ? `NEW COMPANY: ${cleanCompany}` : cleanCompany,
        ];
    }

    newDeveloper.value = '';
    newDevloperFromDataStructure.value = null;

    emit('update:modelValue', [...localList.value]);
}

function deleteAll (): void {
    localList.value = [];
    emit('update:modelValue', [...localList.value]);
}

function deleteAt (index: number): void {
    if (index < 0 || index >= localList.value.length) return;
    localList.value.splice(index, 1);
    emit('update:modelValue', [...localList.value]);
}

function stripPrefix (value: string): string {
    return value.replace(/^NEW COMPANY:\s*/i, '');
}

const debouncedUpdateNewCompanyName = useDebounceFn(updateNewCompanyName, 300);

function updateNewCompanyName (name: string | undefined, index: number): void {
    const clean = name == null ? '' : name.trim();
    const existingCompany: string | undefined = dataValidationStore.companies.find(
        c => c.toLowerCase() === clean.toLowerCase(),
    );

    if (existingCompany) {
        localList.value[index] = existingCompany;
        emit('update:modelValue', [...localList.value]);
        return;
    }
    localList.value[index] = `NEW COMPANY: ${clean}`;
    emit('update:modelValue', [...localList.value]);
}

watch(() => props.modelValue, (v) => {
    localList.value = v ? [...v] : [];
});

</script>
